import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import strings from "../global/strings";
import { setAuthBanner } from "../global/query-client";

export const sessionQueryKey = ["session"] as const;

// Mirrors billing_system_BE/middleware/auth.js's getUserPayload()
export interface SessionData {
	company_user_id?: string;
	user_role?: "owner" | "user";
	user_id?: number;
	user_info_id?: string;
	full_name?: string;
	first_name?: string;
	last_name?: string;
	user_gender?: string | null;
	user_email?: string;
	user_mobile?: string;
	user_address?: string | null;
	user_city?: string | null;
	user_country?: string | null;
	user_status?: string;
	user_verified?: boolean;
	user_image?: string | null;
	company_id?: string;
	company_info_id?: string;
	company_name?: string;
	company_color_code?: string;
	company_logo?: string;
	company_verified?: boolean;
	// This user's app-login identity (billing_system_BE/models/login.js), added
	// by the logins-table redesign. Distinct from login_info_id, which the
	// middleware spreads in separately (not part of getUserPayload's own return).
	login_info_id?: string | null;
	login_username?: string | null;
	// NOTE: username/password below are the company's IRD identification
	// credentials (company.identification), unrelated to app login — do not
	// repurpose these for login_username/password despite the naming overlap.
	username?: string;
	password?: string;
	registration_type?: string;
	registration_number?: string;
}

async function fetchCurrentUser(): Promise<SessionData> {
	const res = await axios.get(`${strings.url}user/current`, {
		withCredentials: true,
	});
	if (!res.data.success) throw new Error(res.data.message);
	return res.data.result;
}

export function useSession() {
	return useQuery({
		queryKey: sessionQueryKey,
		queryFn: fetchCurrentUser,
		retry: false,
		staleTime: 5 * 60 * 1000,
	});
}

interface LoginPayload {
	username: string;
	password: string;
	company_code?: string;
}

export interface LoginResult {
	status: boolean;
	// present when the user belongs to multiple companies — they must pick one
	data?: unknown[];
	// 100 = company not yet IRD-verified; ProtectedRoute redirects to /verification
	errorStatus?: number;
}

async function postLogin(loginInfo: LoginPayload): Promise<LoginResult> {
	const res = await axios.post(`${strings.url}user/login`, loginInfo, {
		withCredentials: true,
	});
	if (!res.data.success) throw new Error(res.data.message);

	if (res.data.result) {
		// Multiple companies — caller presents a picker then re-submits with company_code
		return { status: false, data: res.data.result };
	}

	localStorage.setItem("irdStatus", JSON.stringify(res.data.irdStatus));
	return { status: true, errorStatus: res.data.errorStatus };
}

export function useLogin() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: postLogin,
		onSuccess: (result) => {
			if (result.status) {
				queryClient.invalidateQueries({ queryKey: sessionQueryKey });
			}
		},
	});
}

async function deleteLogout(): Promise<string> {
	const res = await axios.delete(`${strings.url}user/logout`, {
		withCredentials: true,
	});
	if (!res.data.success) throw new Error(res.data.message);
	return res.data.message;
}

export function useLogout() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: deleteLogout,
		onSuccess: (message) => {
			localStorage.clear();
			setAuthBanner({ message, type: "success" });
			queryClient.setQueryData(sessionQueryKey, null);
		},
	});
}

interface UpdatePasswordPayload {
	user_info_id?: string;
	oldPassword: string;
	password: string;
	confirm_password: string;
}

async function putUpdatePassword(data: UpdatePasswordPayload): Promise<string> {
	const res = await axios.put(`${strings.url}user/change/password`, data, {
		withCredentials: true,
	});
	if (!res.data.success) throw new Error(res.data.message);
	return res.data.message;
}

export function useUpdatePassword() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: putUpdatePassword,
		onSuccess: (message) => {
			localStorage.clear();
			setAuthBanner({ message, type: "success" });
			queryClient.setQueryData(sessionQueryKey, null);
		},
	});
}

interface ForgotPasswordPayload {
	username: string;
}

async function postForgotPassword(data: ForgotPasswordPayload): Promise<string> {
	const res = await axios.post(`${strings.url}user/forgot/password`, data);
	if (!res.data.success) throw new Error(res.data.message);
	return res.data.message;
}

export function useForgotPassword() {
	return useMutation({
		mutationFn: postForgotPassword,
	});
}

interface ResetPasswordPayload {
	password: string;
	confirm_password: string;
	verificationToken: string;
}

async function putResetPassword(data: ResetPasswordPayload): Promise<string> {
	const res = await axios.put(`${strings.url}user/reset/password`, data);
	if (!res.data.success) throw new Error(res.data.message);
	return res.data.message;
}

export function useResetPassword() {
	return useMutation({
		mutationFn: putResetPassword,
	});
}

interface UpdateIdentificationsPayload {
	username: string;
	password: string;
	verification?: boolean;
}

async function putUpdateCompanyIdentifications(
	data: UpdateIdentificationsPayload,
): Promise<string> {
	const res = await axios.put(`${strings.url}identification/update`, data, {
		withCredentials: true,
	});
	if (!res.data.success) throw new Error(res.data.message);
	return res.data.message;
}

export function useUpdateCompanyIdentifications() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: putUpdateCompanyIdentifications,
		onSuccess: () => {
			// Refetch session so company_verified flips to true for ProtectedRoute
			queryClient.invalidateQueries({ queryKey: sessionQueryKey });
		},
	});
}
